import { DeviceTypes } from '@eppendorf/app-configs';
import { DeviceDTO } from '@eppendorf/vnls-device-service-utils';
import { Icon, IconSizeClasses, Tooltip } from '@eppendorf/vnls-react-components';
import { ButtonV2 } from '@eppendorf/vnls-react-components-preview';
import { type MouseEvent, type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAdditionalFeatureIsEnabled } from '$components/additional-feature-guard/additional-feature-is-enabled';
import { useDeviceType } from '$components/app-config/useDeviceType';
import {
  ReportHubBetaAccessDialog,
  ReportHubUpsellDialog,
} from '$features/compliance-upsell/report-hub-upsell-nav-item';
import {
  MonitoringReportDialogs,
  MonitoringReportDialogProps,
  MonitoringReportSuccessDialog,
} from '$features/monitoring/report/monitoring-report-dialogs';
import { useCreateReport } from '$features/monitoring/report/report.api';
import { canCreateReport } from '$features/monitoring/report/utils/can-create-report';
import {
  isEssentialsPlan,
  useGetSubscription,
} from '$features/subscription/subscription-api';

interface MonitoringCreateReportActionProps {
  device: DeviceDTO;
}

export function MonitoringCreateReportAction({
  device,
}: MonitoringCreateReportActionProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isUpsellDialogOpen, setIsUpsellDialogOpen] = useState(false);
  const [isBetaAccessDialogOpen, setIsBetaAccessDialogOpen] = useState(false);

  const deviceType = useDeviceType(device);
  const createReportMutation = useCreateReport();
  const { data: subscription } = useGetSubscription();
  const hasReportHubAccess = useAdditionalFeatureIsEnabled('REPORT_HUB_ACCESS');

  if (!canCreateReport(deviceType as DeviceTypes)) {
    return <div className="flex flex__jc--center">-</div>;
  }

  const handleOpenDialog = (e: MouseEvent) => {
    e.stopPropagation();
    if (isEssentialsPlan(subscription)) {
      setIsUpsellDialogOpen(true);
      return;
    }

    if (!hasReportHubAccess) {
      setIsBetaAccessDialogOpen(true);
      return;
    }

    setIsDialogOpen(true);
  };

  const handleSave: MonitoringReportDialogProps['onSave'] = async (data) => {
    await createReportMutation.mutateAsync({
      startDate: data.dateRange.from.toISOString(),
      endDate: data.dateRange.to.toISOString(),
      serialNumber: device.serialNumber,
      manufacturer: device.manufacturer,
      notes: data.notes,
      deviceName: data.deviceName,
      deviceModel: data.deviceModel,
      reportType: 'Stability',
      selectedTelemetryIds: data.selectedTelemetryIds,
    });

    setIsDialogOpen(false);
    setIsSuccessDialogOpen(true);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- div used as event propagation barrier to prevent row click from firing when interacting with the dialog portal
    <div className="flex flex__jc--center" onClick={(e) => e.stopPropagation()}>
      <Tooltip
        trigger={
          <ButtonV2
            size="sm"
            variant="ghost"
            iconOnly
            onClick={handleOpenDialog}
            aria-label={t('monitoring.report.createButton')}
          >
            <Icon name="document-generic-release" size={IconSizeClasses.Small} />
          </ButtonV2>
        }
      >
        {t('monitoring.report.createButton')}
      </Tooltip>

      <MonitoringReportDialogs
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        deviceId={device.serialNumber}
        manufacturer={device.manufacturer}
        closeOnOutsideClick
        onSave={handleSave}
      />

      <MonitoringReportSuccessDialog
        isOpen={isSuccessDialogOpen}
        onOpenChange={setIsSuccessDialogOpen}
        onViewDetails={() => navigate('/report-hub/stability')}
      />
      <ReportHubUpsellDialog
        isOpen={isUpsellDialogOpen}
        onOpenChange={setIsUpsellDialogOpen}
      />
      <ReportHubBetaAccessDialog
        isOpen={isBetaAccessDialogOpen}
        onOpenChange={setIsBetaAccessDialogOpen}
      />
    </div>
  );
}
